"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { supabase } from "@/lib/supabase"
import type { PreviewData } from "@/lib/types"
import { toast } from "sonner"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { MoreVertical, Pencil, Trash2, Share2, FolderKanban, Eye, Blocks } from "lucide-react"
import { DeleteDialog } from "@/components/preview/DeleteDialog"
import { EditDialog } from "@/components/preview/EditDialog"
import { ShareDialog } from "@/components/preview/ShareDialog"
import { TagDisplay } from "@/components/ui/tag-display"
import { BUCKET_NAME } from "@/lib/constants"
import { useTranslation } from "react-i18next";

interface PreviewCardProps {
  preview: PreviewData
  onDelete?: (id: string) => void
  onEdit?: (preview: PreviewData) => void
  showProjectTag?: boolean // New prop to control project tag visibility
}

export function PreviewCard({ preview, onDelete, onEdit, showProjectTag = true }: PreviewCardProps) {  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false)
  const [newLabel, setNewLabel] = useState(preview.name)
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(preview.project_id || null)
  const [editTags, setEditTags] = useState<string[]>(preview.tags || [])
  const [isSharable, setIsSharable] = useState(preview.is_sharable || false)
  const [isUpdatingShareable, setIsUpdatingShareable] = useState(false)
  const { t } = useTranslation("common");
  useEffect(() => {
    setIsSharable(preview.is_sharable || false)
    setSelectedProjectId(preview.project_id || null)
    setEditTags(preview.tags || [])
  }, [preview.is_sharable, preview.project_id, preview.tags])

  const handleDelete = async () => {
    try {
      const { error: storageError } = await supabase.storage.from(BUCKET_NAME).remove([preview.asset_path])
      if (storageError) {
        console.error("Error deleting asset from storage:", storageError)
      }
      const { error } = await supabase.from("previews").delete().eq("id", preview.id)
      if (error) throw error
      toast.success(t("common:previewCard.toast.deleted"));
      if (onDelete) {
        onDelete(preview.id)
      }
    } catch (error) {
      console.error("Error deleting preview:", error)
      toast.error(t("common:previewCard.toast.deleteError"));
    }
  };

  const handleEdit = async () => {
    try {
      const { error } = await supabase.from("previews").update({ 
        name: newLabel,
        project_id: selectedProjectId,
        tags: editTags
      }).eq("id", preview.id)
      if (error) throw error
      toast.success(t("common:previewCard.toast.updated"));
      if (onEdit) {
        onEdit({ ...preview, name: newLabel, project_id: selectedProjectId, tags: editTags })
      }
      setIsEditDialogOpen(false)
    } catch (error) {
      console.error("Error updating preview:", error)
      toast.error(t("common:previewCard.toast.updateError"));
    }
  }

  const handleShareToggle = async (enabled: boolean) => {
    setIsUpdatingShareable(true)
    try {
      const { error } = await supabase.from("previews").update({ is_sharable: enabled }).eq("id", preview.id)
      if (error) throw error
      setIsSharable(enabled)
      if (onEdit) {
        onEdit({ ...preview, is_sharable: enabled })
      }
      toast.success(
        enabled
          ? t("common:previewCard.toast.shareEnabled")
          : t("common:previewCard.toast.shareDisabled")
      )
    } catch (error) {
      console.error("Error updating sharing status:", error)
      toast.error(t("common:previewCard.toast.shareError"))
    } finally {
      setIsUpdatingShareable(false)
    }
  }

  return (
    <>
      <Card className="overflow-hidden h-full flex flex-col group hover:shadow-lg transition-all duration-300 pt-0">
        <CardContent className="p-0">
          <div className="relative bg-gradient-to-br from-muted/50 to-muted h-48 flex items-center justify-center overflow-hidden">
            {preview.asset_path ? (
              <>
                <div
                  className="absolute inset-0 bg-cover bg-center blur-md scale-105 opacity-20"
                  style={{ backgroundImage: `url(${preview.asset_path})` }}
                />
                <div className="relative z-10 h-full w-full overflow-hidden">
                  <img
                    src={preview.asset_path || "/placeholder.svg"}
                    alt={preview.name}
                    className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
              </>
            ) : (
              <div className="text-muted-foreground flex flex-col items-center">
                <Eye className="h-8 w-8 mb-2" />
                <span className="text-sm">{t("common:previewCard.noImage")}</span>
              </div>
            )}            {/* Status badges */}
            <div className="absolute top-2 left-2 flex flex-col gap-1 z-20">
              {preview.is_sharable && (
                <Badge variant="secondary" className="bg-green-500/90 text-white text-xs h-6 w-6 rounded-full p-0 flex items-center justify-center">
                  <Share2 className="h-3 w-3" />
                </Badge>
              )}
              {preview.sub_preview_id && (
                <Badge variant="secondary" className="bg-blue-500/90 text-white text-xs h-6 w-6 rounded-full p-0 flex items-center justify-center">
                  <Blocks className="h-3 w-3" />
                </Badge>
              )}
            </div>

            {/* Actions menu */}
            <div className="absolute top-2 right-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8 bg-background/80 backdrop-blur-sm rounded-full hover:bg-background/90"
                  >
                    <MoreVertical className="h-4 w-4" />
                    <span className="sr-only">{t("common:previewCard.menu")}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setIsEditDialogOpen(true)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    {t("common:previewCard.edit")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsShareDialogOpen(true)}>
                    <Share2 className="mr-2 h-4 w-4" />
                    {t("common:previewCard.share")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setIsDeleteDialogOpen(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common:previewCard.delete")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>

        <CardHeader className="pb-2">
          <CardTitle className="text-lg leading-tight line-clamp-2">{preview.name}</CardTitle>
          <CardDescription className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{new Date(preview.created_at).toLocaleDateString()}</span>
              <Badge variant="outline" className="text-xs">
                {preview.model?.name || t("common:previewCard.unknownModel")}
              </Badge>
            </div>            {showProjectTag && preview.project && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <FolderKanban className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{preview.project.name}</span>
              </div>
            )}
            {preview.tags && preview.tags.length > 0 && (
              <TagDisplay 
                tags={preview.tags} 
                maxDisplay={2} 
                size="sm" 
                className="mt-1"
              />
            )}
          </CardDescription>
        </CardHeader>

        <CardFooter className="mt-auto">
          <Button asChild className="w-full" size="sm">
            <Link to={`/viewer/${preview.id}`}>
              <Eye className="mr-2 h-4 w-4" />
              {t("common:previewCard.openPreview")}
            </Link>
          </Button>
        </CardFooter>
      </Card>

      <DeleteDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDelete}
        previewName={preview.name}
      />      <EditDialog
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onEdit={handleEdit}
        newLabel={newLabel}
        setNewLabel={setNewLabel}
        selectedProjectId={selectedProjectId}
        onProjectSelect={setSelectedProjectId}
        tags={editTags}
        onTagsChange={setEditTags}
      />

      <ShareDialog
        isOpen={isShareDialogOpen}
        onOpenChange={setIsShareDialogOpen}
        isSharable={isSharable}
        onShareToggle={handleShareToggle}
        isUpdatingShareable={isUpdatingShareable}
        previewId={preview.id}
      />
    </>
  )
}
